import { useCallback, useMemo } from 'react';

import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import ModalDelete from '@/components/organisms/Modals/ModalDelete';
import ModalWarn from '@/components/organisms/Modals/ModalWarn';
import { useAllRules } from '@/hooks/useAllRules';
import { useBundles } from '@/modules/bundles/hooks/useBundles';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Rule } from '@/shared/types/rules';

const ModalBundleDelete = ({ bundleId, deskId, name }: Props) => {
  const navigate = useNavigate();
  const { deleteBundle, deleteStatus } = useBundles(deskId, bundleId);
  const dispatch = useDispatch();
  const { status, getRulesConnectedToBundle } = useAllRules();
  const slug = useSelector(selectAccountSlug);

  const handleDelete = useCallback(() => {
    deleteBundle(bundleId, {
      onSettled: () => {
        dispatch(popModal());
      },
    });
  }, [bundleId, deleteBundle, dispatch]);

  const subtitle = <Trans i18nKey="bundles.delete_warning" values={[name]} />;

  const connectedRules = useMemo(
    () => getRulesConnectedToBundle(bundleId),
    [bundleId, getRulesConnectedToBundle]
  );

  if (status !== 'pending' && connectedRules.length === 0) {
    return <ModalDelete confirm subtitle={subtitle} onDelete={handleDelete} />;
  }

  const isDeleting = deleteStatus === 'pending';

  return (
    <ModalWarn isSubmitting={isDeleting}>
      <AlertWithReferences<Partial<Rule>>
        referenceProp="rule_id"
        references={connectedRules}
        labelKey="name"
        onRefClick={(rule_id) => {
          dispatch(popModal());
          navigate(`/${slug}/environments/${deskId}/rules/${rule_id}`);
        }}
      >
        <Trans i18nKey={'bundles.delete_warning_not_empty'} values={{ 0: name }} />
      </AlertWithReferences>
    </ModalWarn>
  );
};

export default ModalBundleDelete;

type Props = { bundleId: string; deskId: string; name: string };
